import React, { useRef } from 'react';
import cn from 'classnames';

import { useQueryActiveSitesData } from '@hooks/sites/useQueryActiveSitesData';
import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	resetActiveCustomPolygons,
	resetActiveMarkers,
	setActiveSiteMarker,
	setInfoWinPosition,
	setMarkerForShortInfo,
	setSelectedSiteIdsByShiftKey,
	setShowMarkerFullInfo,
	setShowMarkerShortInfo,
} from '@slices/map';

import { isEqual, shortenNumber } from '@utils/common';
import { getMarkerTechType } from '@utils/getMarkerTechType';
import { getClusterStyle } from '../helpers/getClusterStyles';

import { ISiteProfile } from '@interfaces/cells/siteProfile';
import { ISiteMarker } from '@interfaces/map/map';

import { EMapMode, EMarkerTypes } from '@enums/map';

import styles from '../Markers.module.scss';

interface ISiteMarkerProps {
	handleClickOnCluster: (marker: ISiteMarker) => void;
	marker: ISiteMarker;
	setIsVisibleSiteInfo?: (isVisible: boolean) => void;
}

const SiteMarker = ({
	handleClickOnCluster,
	marker,
	setIsVisibleSiteInfo,
}: ISiteMarkerProps) => {
	const {
		activeSiteMarkers,
		editingSite,
		isShiftHeld,
		mapMode,
		siteIdsWithinActivePolygons,
		isShowSiteIds,
		showMarkerShortInfo,
		showMarkerFullInfo,
		appliedSitesFiltersValues: { technologies },
	} = useAppSelector(map);

	const dispatch = useAppDispatch();

	const { prefetchSite } = useQueryActiveSitesData();

	const markerRef: React.Ref<HTMLDivElement> = useRef(null);

	const isValidMarker = marker?.technologies?.length > 0;
	const isCluster = marker.markerType !== EMarkerTypes.point && !!marker.count;
	const isActiveMarker = (
		(!isCluster &&
			(showMarkerFullInfo || showMarkerShortInfo) &&
			(!!activeSiteMarkers.find((id) => id === marker.id)))
		|| editingSite?.id === marker.id
	);
	const isMarkerInsideActivePolygon = !!siteIdsWithinActivePolygons.find((id) => id === marker.id);

	const technology = !isCluster && isValidMarker
		? getMarkerTechType(marker?.technologies, technologies)
		: '';

	const clusterCount = isCluster ?? marker.count ? shortenNumber(marker.count) : '';
	const clusterCountStyle = isCluster && clusterCount && getClusterStyle(clusterCount);

	const markerClassname = cn(styles[technology as string], {
		[styles.activeCustomMarker]: isActiveMarker || isMarkerInsideActivePolygon,
		[styles.customMarker]: true,
		[String(clusterCountStyle)]: isCluster,
		[styles.clusterStyle]: isCluster,
		[styles.editingLocation]: editingSite?.id === marker.id,
	});

	const handleClickSite = () => {
		if (mapMode === EMapMode.addMarker || editingSite?.id === marker.id) return;

		if (isCluster) {
			handleClickOnCluster(marker);

			return;
		}

		dispatch(setShowMarkerFullInfo(true));
		dispatch(setMarkerForShortInfo(null));
		dispatch(resetActiveCustomPolygons());
		dispatch(setInfoWinPosition(null));
		dispatch(setActiveSiteMarker({
			site: marker as unknown as ISiteProfile,
			isShift: isShiftHeld,
		}));

		dispatch(setSelectedSiteIdsByShiftKey({
			siteId: marker.id,
			isShift: isShiftHeld,
		}));

		if (setIsVisibleSiteInfo) {
			setIsVisibleSiteInfo(true);
		}
	};

	const handleRightClickMarker = (e: React.MouseEvent<HTMLDivElement>) => {
		if (isShiftHeld) return;

		dispatch(setInfoWinPosition({
			x: e.clientX,
			y: e.clientY,
		}));
		dispatch(setMarkerForShortInfo(marker as unknown as Partial<ISiteProfile>));

		if (!isCluster) {
			dispatch(setShowMarkerShortInfo(true));
			dispatch(setActiveSiteMarker({
				site: marker as unknown as ISiteProfile,
				isShift: isShiftHeld,
				showSiteShortInfo: true,

			}));
		}
	};

	const onMouseEnter = () => {
		if (!isCluster) void prefetchSite(marker.id);
	};

	useOnClickOutside({
		refs: [markerRef],
		handler: () => {
			if (!isShiftHeld) {
				dispatch(setMarkerForShortInfo(null));
				dispatch(setInfoWinPosition(null));
				dispatch(resetActiveMarkers());
			}
		},
		disableClickOutside: false,
		triggerMouseButton: 2,
	});

	return (
		<div
			ref={markerRef}
			className={markerClassname}
			onClick={handleClickSite}
			onContextMenu={handleRightClickMarker}
			onMouseEnter={onMouseEnter}
			style={{
				cursor: mapMode === EMapMode.addMarker ? 'crosshair' : 'pointer',
			}}
		>
			{clusterCount}
			{isShowSiteIds && !isCluster && <div className={styles.siteId}>{marker.siteId}</div>}
		</div>
	);
};

export default React.memo(SiteMarker, (prevProps, nextProps) => isEqual(prevProps, nextProps));
